import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useSelector } from 'react-redux';
import { ENTERPRISE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SALES_SEAT, SERVICE_SEAT } from 'self-service-api/constants/UnitNames';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppSelector } from '../../reducers';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { showPricePerMonthPerSeatSelector } from '../../selectors/MerchCardSelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../selectors/PricingSelectors';
import { productLevelForCoreSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasHigherProductLevelInProductInventorySelector, hasProductLineInBundledProductInventorySelector, hasProductLineSelectorInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { coreSeatSkuForProductLevelSelector, includedCoreSeatsQuantityForProductVersionSelector, includedSeatQuantityBySkuIdSelector, isQuantifiableHubSelector, productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const IncludedSeatsDisplay = ({
  skuId,
  quantityPackSkuId,
  quantityPackQuantity,
  queryParamTerm
}) => {
  const isAppReady = useGlobalLoading();
  const productConfigurations = [createProductConfig({
    skuId
  })];
  if (quantityPackSkuId && quantityPackQuantity && quantityPackQuantity > 0) productConfigurations.push(createProductConfig({
    skuId: quantityPackSkuId,
    quantity: quantityPackQuantity
  }));
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const includedCoreSeats = useAppSelector(state => {
    return includedCoreSeatsQuantityForProductVersionSelector(state, {
      productVersionSkuId: skuId
    });
  });
  const includedSalesSeats = useAppSelector(state => {
    return includedSeatQuantityBySkuIdSelector(state, {
      skuId,
      seatUnitName: SALES_SEAT
    });
  });
  const includedServiceSeats = useAppSelector(state => {
    return includedSeatQuantityBySkuIdSelector(state, {
      skuId,
      seatUnitName: SERVICE_SEAT
    });
  });
  const includedSeats = includedCoreSeats + includedSalesSeats + includedServiceSeats;
  const {
    apiName,
    productLine,
    productLevel
  } = useSelector(state => {
    return productForSkuIdSelector(state, {
      skuId
    });
  });
  const ownsProductLineViaProducts = useAppSelector(state => {
    return hasProductLineSelectorInProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLineViaBundle = useAppSelector(state => {
    return hasProductLineInBundledProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLine = ownsProductLineViaProducts || ownsProductLineViaBundle;
  const isQuantifiableHub = useAppSelector(state => isQuantifiableHubSelector(state, {
    skuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const additionalSeatsPrice = useAppSelector(state => {
    if (!isUngatedForSeats) return 0;

    // core seat product level owned or merch card's product level, whichever higher
    const productLevelForCoreSeats = productLevelForCoreSeatsSelector(state, {
      productConfigurations
    });
    const coreSeatSku = coreSeatSkuForProductLevelSelector(state, {
      productLevel: productLevelForCoreSeats
    });
    if (!coreSeatSku) return 0;
    return calculatedPriceForProductConfigurationsInListSelector(state, {
      allProductConfigurations: productConfigurations,
      productConfigurations: [createProductConfig({
        skuId: coreSeatSku.skuId
      })],
      queryParamTerm,
      displayedCurrencyCode: currencyCode
    }).monthly.calculatedPrice;
  });
  const showPricePerMonthPerSeat = useSelector(state => {
    if (!isAppReady) {
      return false;
    }
    return showPricePerMonthPerSeatSelector(state, {
      skuId
    });
  });
  const showIncludedSeats = isUngatedForSeats && !isQuantifiableHub && (
  // Show "Included Y seats" on ALL Non-quantifiable hubs' Pro/Ent card
  productLevel === PROFESSIONAL || productLevel === ENTERPRISE ||
  // Show "Included Y seats" on Starter cards only if they DO NOT OWN HUB [and NOT in "/mo/seat" format]
  productLevel === STARTER && !ownsProductLine && !showPricePerMonthPerSeat);
  const isDowngrade = useAppSelector(state => {
    return hasHigherProductLevelInProductInventorySelector(state, {
      productLine,
      productLevel
    });
  });

  // Show "Additional Seats start at $X/mo" for all non-quantifiable hubs' cards that are NOT downgrades and NOT in the "/mo/seat" format
  const showAdditionalSeatsPriceSubtext = isUngatedForSeats && additionalSeatsPrice > 0 && !isQuantifiableHub && !isDowngrade && !showPricePerMonthPerSeat;
  if (!showIncludedSeats && !showAdditionalSeatsPriceSubtext) {
    return null;
  }
  return /*#__PURE__*/_jsxs(View, {
    marginBlockStart: "600",
    children: [showIncludedSeats ? /*#__PURE__*/_jsx(Text, {
      typography: "body-125",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `merchCards.pricing.includes.seats.${productLevel}`,
        options: {
          count: includedSeats
        }
      })
    }) : null, showAdditionalSeatsPriceSubtext ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "merchCards.pricing.subtexts.additionalSeatsStartAt_jsx",
      options: {
        seleniumId: apiName,
        currencyCode,
        additionalSeatsPrice
      },
      elements: {
        PriceDisplay
      }
    }) : null]
  });
};
export default IncludedSeatsDisplay;